import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    Checkbox,
    FormControlLabel,
    Button,
    Tooltip,
    Menu,
    IconButton,
    useMediaQuery,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { supabase } from '../utils/supabase';
import { STATS_SEASON as RANKINGS_SEASON } from '../config/season';
import { useLeague } from '../contexts/LeagueContext';
import { useAuth } from '../contexts/AuthContext';
import YahooConnect from '../components/YahooConnect';

const PUNT_CATEGORIES = [
    { key: 'points_z', label: 'PTS', fullName: 'Points' },
    { key: 'three_pointers_z', label: '3PM', fullName: 'Three Pointers' },
    { key: 'rebounds_z', label: 'REB', fullName: 'Rebounds' },
    { key: 'assists_z', label: 'AST', fullName: 'Assists' },
    { key: 'steals_z', label: 'STL', fullName: 'Steals' },
    { key: 'blocks_z', label: 'BLK', fullName: 'Blocks' },
    { key: 'fg_percentage_z', label: 'FG%', fullName: 'Field Goal %' },
    { key: 'ft_percentage_z', label: 'FT%', fullName: 'Free Throw %' },
    { key: 'turnovers_z', label: 'TO', fullName: 'Turnovers' },
];

const Rankings = () => {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [periodType, setPeriodType] = useState('season');
    const [puntedCategories, setPuntedCategories] = useState([]);
    const [selectedPlayers, setSelectedPlayers] = useState(new Set());
    const [selectedOpponentTeam, setSelectedOpponentTeam] = useState('');
    const [showHighlightedOnly, setShowHighlightedOnly] = useState(false);
    const [teamMenuAnchor, setTeamMenuAnchor] = useState(null);
    const [teamFilter, setTeamFilter] = useState('all');
    const [positionFilter, setPositionFilter] = useState('all');
    const [orderBy, setOrderBy] = useState('total_value');
    const [order, setOrder] = useState('desc');
    const { userTeamPlayers, leagueTeams } = useLeague();
    const { isAuthenticated } = useAuth();

    // Detect mobile for minor adjustments
    const isMobile = useMediaQuery('(max-width:600px)');

    // Create a Set of user's team player IDs for quick lookup
    const userTeamPlayerIds = useMemo(() => {
        const ids = new Set();
        if (userTeamPlayers && userTeamPlayers.length > 0) {
            userTeamPlayers.forEach(player => {
                if (player.nbaPlayerId) {
                    ids.add(player.nbaPlayerId);
                }
            });
        }
        return ids;
    }, [userTeamPlayers]);

    // Create a Set of opponent team player IDs for quick lookup
    const opponentTeamPlayerIds = useMemo(() => {
        const ids = new Set();
        if (selectedOpponentTeam && leagueTeams && leagueTeams.length > 0) {
            const opponentTeam = leagueTeams.find(team => team.name === selectedOpponentTeam);
            if (opponentTeam && opponentTeam.players) {
                opponentTeam.players.forEach(player => {
                    if (player.nbaPlayerId) {
                        ids.add(player.nbaPlayerId);
                    }
                });
            }
        }
        return ids;
    }, [selectedOpponentTeam, leagueTeams]);

    const isUserTeamPlayer = useCallback((player) => {
        return userTeamPlayerIds.has(player.player_id);
    }, [userTeamPlayerIds]);

    const isOpponentTeamPlayer = useCallback((player) => {
        return opponentTeamPlayerIds.has(player.player_id);
    }, [opponentTeamPlayerIds]);

    // Get unique teams and positions from players
    const { uniqueTeams, uniquePositions } = useMemo(() => {
        const teams = new Set();
        const positions = new Set();
        players.forEach(player => {
            if (player.team_abbreviation) teams.add(player.team_abbreviation);
            if (player.position && player.position !== '-') {
                const posArray = player.position.split(',');
                posArray.forEach(pos => positions.add(pos.trim()));
            }
        });
        return {
            uniqueTeams: Array.from(teams).sort(),
            uniquePositions: Array.from(positions).sort(),
        };
    }, [players]);

    // Calculate adjusted rankings based on punted categories (on full list)
    const adjustedPlayersFull = useMemo(() => {
        if (puntedCategories.length === 0) {
            return players.map((p, idx) => ({
                ...p,
                originalRank: idx + 1,
                adjustedTotalValue: p.total_value,
                adjustedRank: idx + 1,
                rankChange: 0,
                valueChange: 0,
            }));
        }

        const allCategories = PUNT_CATEGORIES.map(c => c.key);
        const includedCategories = allCategories.filter(c => !puntedCategories.includes(c));
        const n = includedCategories.length;
        const sqrtN = Math.sqrt(n);

        const playersWithAdjusted = players.map((player) => {
            const sum = includedCategories.reduce((acc, cat) => acc + (player[cat] || 0), 0);
            const adjustedTotalValue = sum / sqrtN;

            return {
                ...player,
                originalRank: players.indexOf(player) + 1,
                adjustedTotalValue: Number(adjustedTotalValue.toFixed(2)),
                valueChange: Number((adjustedTotalValue - player.total_value).toFixed(2)),
            };
        });

        const sorted = [...playersWithAdjusted].sort((a, b) => b.adjustedTotalValue - a.adjustedTotalValue);

        return sorted.map((player, idx) => ({
            ...player,
            adjustedRank: idx + 1,
            rankChange: player.originalRank - (idx + 1),
        }));
    }, [players, puntedCategories]);

    // Apply filters (team, position, highlighted only) but keep full ranks
    const displayedPlayers = useMemo(() => {
        let filtered = adjustedPlayersFull;

        if (showHighlightedOnly) {
            if (selectedOpponentTeam) {
                filtered = filtered.filter(p => isUserTeamPlayer(p) || isOpponentTeamPlayer(p));
            } else {
                filtered = filtered.filter(p => isUserTeamPlayer(p));
            }
        }

        if (teamFilter !== 'all') {
            filtered = filtered.filter(p => p.team_abbreviation === teamFilter);
        }

        if (positionFilter !== 'all') {
            filtered = filtered.filter(p => p.position && p.position.includes(positionFilter));
        }

        return filtered;
    }, [adjustedPlayersFull, showHighlightedOnly, selectedOpponentTeam, teamFilter, positionFilter, isUserTeamPlayer, isOpponentTeamPlayer]);

    // Apply user sorting to displayed players
    const sortedPlayers = useMemo(() => {
        if (!displayedPlayers.length) return [];

        const sorted = [...displayedPlayers].sort((a, b) => {
            let aVal = a[orderBy];
            let bVal = b[orderBy];

            if (aVal === null || aVal === undefined) aVal = order === 'asc' ? Infinity : -Infinity;
            if (bVal === null || bVal === undefined) bVal = order === 'asc' ? Infinity : -Infinity;

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return order === 'asc' 
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            }

            return order === 'asc' ? aVal - bVal : bVal - aVal;
        });

        return sorted;
    }, [displayedPlayers, orderBy, order]);

    // Auto-switch sorting when punting
    useEffect(() => {
        if (puntedCategories.length > 0) {
            setOrderBy('adjustedTotalValue');
            setOrder('desc');
        } else {
            setOrderBy('total_value');
            setOrder('desc');
        }
    }, [puntedCategories.length]);

    // Team impact calculation
    const teamImpact = useMemo(() => {
        if (!userTeamPlayers || userTeamPlayers.length === 0 || puntedCategories.length === 0) {
            return null;
        }

        const teamPlayers = sortedPlayers.filter(p => isUserTeamPlayer(p));
        
        if (teamPlayers.length === 0) return null;

        const totalValueChange = teamPlayers.reduce((sum, p) => sum + p.valueChange, 0);
        const avgValueChange = totalValueChange / teamPlayers.length;
        const playersImproved = teamPlayers.filter(p => p.valueChange > 0).length;
        const playersDeclined = teamPlayers.filter(p => p.valueChange < 0).length;

        return {
            totalValueChange: Number(totalValueChange.toFixed(2)),
            avgValueChange: Number(avgValueChange.toFixed(2)),
            playersImproved,
            playersDeclined,
            totalPlayers: teamPlayers.length,
        };
    }, [sortedPlayers, userTeamPlayers, puntedCategories.length, isUserTeamPlayer]);

    const handlePuntToggle = (categoryKey) => {
        setPuntedCategories(prev => {
            if (prev.includes(categoryKey)) {
                return prev.filter(c => c !== categoryKey);
            } else {
                return [...prev, categoryKey];
            }
        });
    };

    // Auto-select user's team players
    useEffect(() => {
        if (players.length > 0 && userTeamPlayerIds.size > 0) {
            const teamPlayerIds = new Set();
            players.forEach(player => {
                if (userTeamPlayerIds.has(player.player_id)) {
                    teamPlayerIds.add(player.player_id);
                }
            });
            setSelectedPlayers(teamPlayerIds);
        }
    }, [players, userTeamPlayerIds]);

    const handlePlayerToggle = (playerId) => {
        setSelectedPlayers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(playerId)) {
                newSet.delete(playerId);
            } else {
                newSet.add(playerId);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        if (selectedPlayers.size === sortedPlayers.length) {
            setSelectedPlayers(new Set());
        } else {
            setSelectedPlayers(new Set(sortedPlayers.map(p => p.player_id)));
        }
    };

    const handleSort = (columnId) => {
        if (columnId === 'select' || columnId === 'rank') return;

        const isDesc = orderBy === columnId && order === 'desc';
        setOrder(isDesc ? 'asc' : 'desc');
        setOrderBy(columnId);
    };

    useEffect(() => {
        const fetchPlayers = async () => {
            try {
                const { data: periodData, error: periodError } = await supabase
                    .from('player_period_averages')
                    .select('*')
                    .eq('season', RANKINGS_SEASON)
                    .eq('period_type', periodType)
                    .limit(250)
                    .order('total_value', { ascending: false });

                if (periodError) throw periodError;

                const { data: yahooData, error: yahooError } = await supabase
                    .from('yahoo_nba_mapping')
                    .select('nba_id, position');

                if (yahooError) throw yahooError;

                const positionMap = new Map();
                yahooData.forEach(player => {
                    positionMap.set(player.nba_id, player.position);
                });

                const playersWithPosition = periodData.map(player => ({
                    ...player,
                    position: positionMap.get(player.player_id) || '-',
                }));

                setPlayers(playersWithPosition || []);
            } catch (error) {
                console.error('Error fetching player rankings:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPlayers();
    }, [periodType]);

    const formatValue = (value, column, player) => {
        if (value === null || value === undefined || value === 0) return '-';

        if (column === 'field_goal_percentage') {
            const fgm = player?.field_goals_per_game || 0;
            const fga = player?.field_goals_attempted_per_game || 0;
            return `${(value * 100).toFixed(1)}% (${fgm.toFixed(1)}/${fga.toFixed(1)})`;
        }
        if (column === 'free_throw_percentage') {
            const ftm = player?.free_throws_per_game || 0;
            const fta = player?.free_throws_attempted_per_game || 0;
            return `${(value * 100).toFixed(1)}% (${ftm.toFixed(1)}/${fta.toFixed(1)})`;
        }

        if (column.endsWith('_z') || column === 'total_value' || column === 'adjustedTotalValue') {
            return typeof value === 'number' ? value.toFixed(2) : value;
        }

        if (typeof value === 'number') {
            return value.toFixed(1);
        }

        return value;
    };

    const getValueBackground = (value, isZScore = false) => {
        if (value === null || value === undefined) return 'transparent';
    
        const maxValue = isZScore ? 3 : 6;
        const minValue = -maxValue;
    
        const clamped = Math.max(minValue, Math.min(maxValue, value));
        const normalized = (clamped - minValue) / (maxValue - minValue);
    
        const hue = normalized * 120;
        const strength = Math.abs(clamped) / maxValue;
        const saturation = 30 + strength * 50;
        const lightness = 85 - strength * 35;
    
        return `hsla(${hue}, ${saturation}%, ${lightness}%, 0.45)`;
    };
    
    const columns = useMemo(() => {
        const baseColumns = [
            { id: 'select', label: '', sortable: false, align: 'center', width: 40, sticky: true, left: 0 },
            { id: 'rank', label: 'Rank', sortable: false, align: 'center', width: 50, sticky: true, left: 40 },
            { id: 'player_name', label: 'Name', sortable: true, align: 'left', width: 160, sticky: true, left: 90 },
            { id: 'position', label: 'Pos', sortable: true, width: 50 },
            { id: 'team_abbreviation', label: 'Team', sortable: true, width: 55 },
            { id: 'total_value', label: 'Value', sortable: true, width: 60 },
        ];

        if (puntedCategories.length > 0) {
            baseColumns.push(
                { id: 'adjustedTotalValue', label: 'Adj Val', sortable: true, width: 60 },
                { id: 'valueChange', label: 'Δ', sortable: true, width: 50 },
                { id: 'rankChange', label: '±Rank', sortable: true, width: 55 }
            );
        }

        const statColumns = [
            { statKey: 'points_per_game', zKey: 'points_z', label: 'PTS', width: 50 },
            { statKey: 'three_pointers_per_game', zKey: 'three_pointers_z', label: '3PM', width: 50 },
            { statKey: 'rebounds_per_game', zKey: 'rebounds_z', label: 'REB', width: 50 },
            { statKey: 'assists_per_game', zKey: 'assists_z', label: 'AST', width: 50 },
            { statKey: 'steals_per_game', zKey: 'steals_z', label: 'STL', width: 50 },
            { statKey: 'blocks_per_game', zKey: 'blocks_z', label: 'BLK', width: 50 },
            { statKey: 'field_goal_percentage', zKey: 'fg_percentage_z', label: 'FG%', width: 110 },
            { statKey: 'free_throw_percentage', zKey: 'ft_percentage_z', label: 'FT%', width: 110 },
            { statKey: 'turnovers_per_game', zKey: 'turnovers_z', label: 'TO', width: 50 },
        ];

        statColumns.forEach(({ statKey, zKey, label, width }) => {
            baseColumns.push(
                { id: statKey, label: label, sortable: true, width: width },
                { id: zKey, label: `${label}Z`, sortable: true, width: 50, isZScore: true }
            );
        });

        return baseColumns;
    }, [puntedCategories.length]);

    if (loading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
                <CircularProgress />
            </Box>
        );
    }

    const getPeriodLabel = (period) => {
        const labels = {
            'season': 'Full Season',
            '60_days': 'Last 60 Days',
            '30_days': 'Last 30 Days',
            '7_days': 'Last 7 Days',
        };
        return labels[period] || period;
    };

    return (
        <Box sx={{ p: 2, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            {/* Header Bar */}
            <Box sx={{
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                pb: 2,
                borderBottom: '2px solid #ddd',
                flexWrap: 'wrap',
                gap: 2,
            }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#003366', fontSize: '1.25rem' }}>
                    Player Rankings 
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {!isAuthenticated && (
                        <YahooConnect variant="button" label="Load from Yahoo" />
                    )}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={periodType}
                            onChange={(e) => {
                                setPeriodType(e.target.value);
                                setLoading(true);
                            }}
                            sx={{ bgcolor: '#fff', fontSize: '0.875rem' }}
                        >
                            <MenuItem value="season">Full Season</MenuItem>
                            <MenuItem value="60_days">Last 60 Days</MenuItem>
                            <MenuItem value="30_days">Last 30 Days</MenuItem>
                            <MenuItem value="7_days">Last 7 Days</MenuItem>
                        </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} sx={{ bgcolor: '#fff', fontSize: '0.875rem' }}>
                            <MenuItem value="all">All Teams</MenuItem>
                            {uniqueTeams.map(team => <MenuItem key={team} value={team}>{team}</MenuItem>)}
                        </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)} sx={{ bgcolor: '#fff', fontSize: '0.875rem' }}>
                            <MenuItem value="all">All Pos</MenuItem>
                            {uniquePositions.map(pos => <MenuItem key={pos} value={pos}>{pos}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <Tooltip 
                        title={!isAuthenticated ? "Login to yahoo to load league teams" : selectedOpponentTeam ? `Highlighting: ${selectedOpponentTeam}` : "Click to select an opponent team"}
                        arrow
                    >
                        <span>
                            <Button
                                variant={selectedOpponentTeam ? 'contained' : 'outlined'}
                                size="small"
                                onClick={(e) => setTeamMenuAnchor(e.currentTarget)}
                                disabled={!isAuthenticated || !leagueTeams || leagueTeams.length === 0}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    bgcolor: selectedOpponentTeam ? '#ff6b35' : 'transparent',
                                    color: selectedOpponentTeam ? '#fff' : '#ff6b35',
                                    borderColor: '#ff6b35',
                                    border: '2px solid #ff6b35',
                                    '&:hover': {
                                        bgcolor: selectedOpponentTeam ? '#e55a2b' : 'rgba(255, 107, 53, 0.1)',
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: '#ccc',
                                        color: '#ccc',
                                    },
                                }}
                            >
                                {selectedOpponentTeam || 'Opponent Team'}
                            </Button>
                        </span>
                    </Tooltip>

                    <Menu anchorEl={teamMenuAnchor} open={Boolean(teamMenuAnchor)} onClose={() => setTeamMenuAnchor(null)}>
                        <MenuItem 
                            onClick={() => { setSelectedOpponentTeam(''); setTeamMenuAnchor(null); }}
                            sx={{ fontSize: '0.875rem', fontStyle: selectedOpponentTeam ? 'normal' : 'italic', color: selectedOpponentTeam ? '#000' : '#999' }}
                        >
                            {selectedOpponentTeam ? 'Clear Selection' : 'No team selected'}
                        </MenuItem>
                        {leagueTeams && leagueTeams.filter(team => !team.is_owned_by_current_login).map(team => (
                            <MenuItem 
                                key={team.key || team.name} 
                                onClick={() => { setSelectedOpponentTeam(team.name); setTeamMenuAnchor(null); }}
                                selected={selectedOpponentTeam === team.name}
                                sx={{ fontSize: '0.875rem' }}
                            >
                                {team.name}
                            </MenuItem>
                        ))}
                    </Menu>

                    <Tooltip 
                        title={showHighlightedOnly ? "Showing only highlighted players" : "Filter to show only highlighted players"}
                        arrow
                    >
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => setShowHighlightedOnly(!showHighlightedOnly)}
                                disabled={!isAuthenticated}
                                sx={{
                                    bgcolor: showHighlightedOnly ? '#4caf50' : 'transparent',
                                    color: showHighlightedOnly ? '#fff' : '#4caf50',
                                    border: '2px solid',
                                    borderColor: showHighlightedOnly ? '#4caf50' : '#4caf50',
                                    '&:hover': {
                                        bgcolor: showHighlightedOnly ? '#45a049' : 'rgba(76, 175, 80, 0.08)',
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: '#ccc',
                                        color: '#ccc',
                                    },
                                }}
                            >
                                <FilterListIcon sx={{ fontSize: '1.2rem' }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                        {selectedPlayers.size} selected
                    </Typography>
                </Box>
            </Box>

            {/* Punt Categories */}
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff', border: '1px solid #ddd', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#003366', minWidth: '100px' }}>
                        Punt Categories:
                    </Typography>
                    {PUNT_CATEGORIES.map((cat) => (
                        <FormControlLabel
                            key={cat.key}
                            control={
                                <Checkbox
                                    checked={puntedCategories.includes(cat.key)}
                                    onChange={() => handlePuntToggle(cat.key)}
                                    size="small"
                                    sx={{ '&.Mui-checked': { color: '#0066cc' } }}
                                />
                            }
                            label={<Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#000' }}>{cat.label}</Typography>}
                            sx={{ m: 0 }}
                        />
                    ))}
                </Box>
                {puntedCategories.length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666', fontSize: '0.75rem' }}>
                        Excluding: {PUNT_CATEGORIES.filter(c => puntedCategories.includes(c.key)).map(c => c.label).join(', ')}
                    </Typography>
                )}
                
                {teamImpact && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: teamImpact.totalValueChange >= 0 ? '#e8f5e9' : '#ffebee', border: `1px solid ${teamImpact.totalValueChange >= 0 ? '#4caf50' : '#f44336'}`, borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>Your Team:</Typography>
                            <Typography variant="body2" sx={{ color: '#000' }}>
                                Total Change: <strong style={{ color: teamImpact.totalValueChange >= 0 ? '#2e7d32' : '#c62828' }}>
                                    {teamImpact.totalValueChange >= 0 ? '+' : ''}{teamImpact.totalValueChange}
                                </strong>
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#000' }}>
                                Avg: <strong>{teamImpact.avgValueChange >= 0 ? '+' : ''}{teamImpact.avgValueChange}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#000' }}>
                                <span style={{ color: '#4caf50' }}>↑{teamImpact.playersImproved}</span>
                                {' / '}
                                <span style={{ color: '#f44336' }}>↓{teamImpact.playersDeclined}</span>
                                {' / '}
                                {teamImpact.totalPlayers}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {displayedPlayers.length === 0 && !loading ? (
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', border: '1px solid #ddd', borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        No {RANKINGS_SEASON} data available for {getPeriodLabel(periodType)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                        Stats will appear after regular-season games are played and the daily update runs.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ bgcolor: '#fff', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                    {/* Single scroll container — one context for both axes with a
                        sticky header. Avoids the nested-scroller jank; uses dvh so the
                        mobile URL bar doesn't clip it, and overscroll:contain to stop
                        the page bouncing when you reach an edge. */}
                    <TableContainer
                        sx={{
                            maxHeight: { xs: 'calc(100dvh - 250px)', sm: 'calc(100dvh - 240px)' },
                            overflow: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            overscrollBehavior: 'contain',
                            scrollbarWidth: 'thin',
                            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#c1c1c1',
                                borderRadius: '4px',
                                '&:hover': { background: '#a8a8a8' },
                            },
                        }}
                    >
                        <Table size="small" stickyHeader sx={{ minWidth: { xs: 1000, md: 1200 }, borderCollapse: 'separate', borderSpacing: 0 }}>
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align={column.align || 'center'}
                                                sx={{
                                                    bgcolor: '#003366',
                                                    color: '#fff',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    py: 1,
                                                    px: 0.5,
                                                    borderBottom: '1px solid #ddd',
                                                    width: column.width || 'auto',
                                                    ...(column.sticky && {
                                                        position: 'sticky',
                                                        left: column.left,
                                                        zIndex: 5, // above sticky body cells (3) and normal header (2)
                                                        minWidth: column.width,
                                                        maxWidth: column.width,
                                                        borderRight: column.id === 'player_name' ? '2px solid #002244' : 'none',
                                                    }),
                                                }}
                                            >
                                                {column.id === 'select' ? (
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedPlayers.size === sortedPlayers.length}
                                                        indeterminate={selectedPlayers.size > 0 && selectedPlayers.size < sortedPlayers.length}
                                                        onChange={handleSelectAll}
                                                        sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' }, '&.MuiCheckbox-indeterminate': { color: '#fff' }, p: 0 }}
                                                    />
                                                ) : column.sortable ? (
                                                    <TableSortLabel
                                                        active={orderBy === column.id}
                                                        direction={orderBy === column.id ? order : 'asc'}
                                                        onClick={() => handleSort(column.id)}
                                                        sx={{
                                                            color: '#fff !important',
                                                            '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                                                        }}
                                                    >
                                                        {column.label}
                                                    </TableSortLabel>
                                                ) : (
                                                    column.label
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedPlayers.map((player) => {
                                        const isMyPlayer = isUserTeamPlayer(player);
                                        const isOpponentPlayer = isOpponentTeamPlayer(player);
                                        const isSelected = selectedPlayers.has(player.player_id);
                                        const displayRank = puntedCategories.length > 0 ? player.adjustedRank : player.originalRank;

                                        let borderLeft = 'none';
                                        let bgColor = '#fff';
                                        let hoverColor = 'rgba(0, 0, 0, 0.03)';
                                        
                                        if (isMyPlayer) {
                                            borderLeft = '3px solid #0066cc';
                                            bgColor = 'rgba(0, 102, 204, 0.2)';
                                            hoverColor = 'rgba(0, 102, 204, 0.12)';
                                        } else if (isOpponentPlayer) {
                                            borderLeft = '3px solid #ff6b35';
                                            bgColor = 'rgba(236, 94, 0, 0.2)';
                                            hoverColor = 'rgba(255, 107, 53, 0.12)';
                                        }
                                        
                                        if (isSelected && !isMyPlayer && !isOpponentPlayer) {
                                            bgColor = 'rgba(0, 102, 204, 0.08)';
                                            hoverColor = 'rgba(0, 102, 204, 0.12)';
                                        }

                                        // Opaque equivalent of the row tint, for the frozen
                                        // left columns (a translucent bg would let scrolled
                                        // cells bleed through the sticky columns).
                                        let solidBg = '#fff';
                                        if (isMyPlayer) solidBg = '#cce0f5';
                                        else if (isOpponentPlayer) solidBg = '#fbdfcc';
                                        else if (isSelected) solidBg = '#ebf3fb';

                                        return (
                                            <TableRow
                                                key={player.id}
                                                onClick={() => handlePlayerToggle(player.player_id)}
                                                sx={{
                                                    bgcolor: bgColor,
                                                    '&:hover': { bgcolor: hoverColor, cursor: 'pointer' },
                                                    borderLeft: borderLeft,
                                                }}
                                            >
                                                {columns.map((column) => {
                                                    let value = column.id === 'rank' ? displayRank : player[column.id];
                                                    let cellColor = '#000';
                                                    let cellBg = 'transparent';

                                                    if (puntedCategories.includes(column.id)) {
                                                        cellColor = '#999';
                                                        cellBg = '#f5f5f5';
                                                    } else if (column.id === 'total_value') {
                                                        cellBg = getValueBackground(value, false);
                                                    } else if (column.id === 'adjustedTotalValue') {
                                                        value = player.adjustedTotalValue;
                                                        cellBg = getValueBackground(value, false);
                                                    } else if (column.id === 'valueChange') {
                                                        value = player.valueChange;
                                                        cellColor = value > 0 ? '#4caf50' : value < 0 ? '#f44336' : '#000';
                                                    } else if (column.id === 'rankChange') {
                                                        value = player.rankChange;
                                                        cellColor = value > 0 ? '#4caf50' : value < 0 ? '#f44336' : '#000';
                                                    } else if (column.isZScore || column.id.endsWith('_z')) {
                                                        const zValue = value || 0;
                                                        cellBg = getValueBackground(zValue, true);
                                                    } else if (column.id === 'position') {
                                                        cellColor = '#666';
                                                    }

                                                    return (
                                                        <TableCell
                                                            key={column.id}
                                                            align={column.align || 'center'}
                                                            sx={{
                                                                py: 0.75,
                                                                px: 0.5,
                                                                fontSize: '0.75rem',
                                                                borderBottom: '1px solid #eee',
                                                                color: cellColor,
                                                                bgcolor: cellBg,
                                                                fontWeight: column.id === 'player_name' ? 600 : 400,
                                                                width: column.width || 'auto',
                                                                ...(column.sticky && {
                                                                    position: 'sticky',
                                                                    left: column.left,
                                                                    zIndex: 3,
                                                                    bgcolor: solidBg,
                                                                    minWidth: column.width,
                                                                    maxWidth: column.width,
                                                                    borderRight: column.id === 'player_name' ? '2px solid #e0e0e0' : 'none',
                                                                }),
                                                            }}
                                                        >
                                                            {column.id === 'select' ? (
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={isSelected}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={() => handlePlayerToggle(player.player_id)}
                                                                    sx={{ p: 0 }}
                                                                />
                                                            ) : column.id === 'rank' ? (
                                                                <Box>
                                                                    {value}
                                                                    {puntedCategories.length > 0 && player.originalRank !== player.adjustedRank && (
                                                                        <Typography component="span" sx={{ fontSize: '0.65rem', color: '#999', ml: 0.5 }}>
                                                                            ({player.originalRank})
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            ) : column.id === 'player_name' ? (
                                                                <Box sx={{ textAlign: 'left' }}>{value}</Box>
                                                            ) : column.id === 'position' ? (
                                                                <Box sx={{ fontSize: '0.7rem', fontWeight: 600 }}>{value || '-'}</Box>
                                                            ) : column.id === 'rankChange' ? (
                                                                <Box>
                                                                    {value > 0 && '↑'}
                                                                    {value < 0 && '↓'}
                                                                    {value > 0 ? `+${value}` : value || '–'}
                                                                </Box>
                                                            ) : column.id === 'valueChange' ? (
                                                                <Box>{value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2)}</Box>
                                                            ) : column.id === 'adjustedTotalValue' ? (
                                                                <Box>{value.toFixed(2)}</Box>
                                                            ) : (
                                                                formatValue(value, column.id, player)
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Optional: Scroll hint for mobile users */}
                    {isMobile && (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 1,
                                bgcolor: 'rgba(255, 255, 255, 0.95)',
                                color: '#666',
                                fontSize: '0.8rem',
                                borderTop: '1px solid #eee',
                            }}
                        >
                            ← Scroll horizontally to view all stats →
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default Rankings;
